<template>
  <div
    class="want-card"
    :class="{ 'theme-dark': isDarkTheme }"
    @click="$emit('click')"
  >
    <!-- subtle decorative glow -->
    <div class="card-glow"></div>

    <div class="left">
      <div class="logo-shell">
        <img
          src="../../public/ai-feature.png"
          class="logo"
          alt="HR3"
        />
      </div>

      <div class="text-block">

        <div class="title-row">
          <h3 class="title">
            Хочу тут работать!
          </h3>

          <span class="badge">
            NEW
          </span>
        </div>

        <p class="subtitle">
          Попросить помощи у нейронной сети красиво оформить сопроводительное письмо.
        </p>

        <div
          v-if="loading"
          class="dots"
        >
          <span></span>
          <span></span>
          <span></span>
        </div>

      </div>
    </div>

    <img
      src="../../public/robot-ai.png"
      class="robot"
      alt="robot"
    />
  </div>
</template>


<script setup>

import {
  ref,
  onMounted,
  onBeforeUnmount
} from "vue";


defineProps({
  loading: {
    type: Boolean,
    default: false
  }
});


defineEmits([
  "click"
]);


/* =========================================================
   LOCAL THEME
========================================================= */

const isDarkTheme = ref(false);

let themeObserver = null;


function updateTheme() {
  isDarkTheme.value =
    document.documentElement
      .getAttribute("data-theme") === "dark";
}


onMounted(() => {

  updateTheme();

  themeObserver =
    new MutationObserver(
      updateTheme
    );

  themeObserver.observe(
    document.documentElement,
    {
      attributes: true,
      attributeFilter: [
        "data-theme"
      ]
    }
  );

});


onBeforeUnmount(() => {
  themeObserver?.disconnect();
});

</script>


<style scoped>

/* =========================================================
   CARD
========================================================= */

.want-card {
  position: relative;

  width: 300px;
  min-height: 112px;

  margin-left: 0;
  margin-top: 20px;

  box-sizing: border-box;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 8px;

  padding:
    14px
    13px
    14px
    15px;

  overflow: hidden;

  cursor: pointer;

  background:
    linear-gradient(
      145deg,
      #ffffff 0%,
      #fbfcfe 100%
    );

  border:
    1px solid
    rgba(25, 40, 65, 0.08);

  border-radius: 18px;

  box-shadow:
    0 4px 16px
    rgba(29, 48, 74, 0.035);

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;
}


.want-card:hover {
  transform:
    translateY(-2px);

  border-color:
    rgba(52, 120, 220, 0.14);

  box-shadow:
    0 9px 24px
    rgba(31, 57, 95, 0.08);
}


/* =========================================================
   DECORATIVE GLOW
========================================================= */

.card-glow {
  position: absolute;

  right: -48px;
  top: -55px;

  width: 145px;
  height: 145px;

  pointer-events: none;

  background:
    radial-gradient(
      circle,
      rgba(76, 111, 236, 0.10) 0%,
      rgba(76, 111, 236, 0.035) 45%,
      transparent 72%
    );

  border-radius: 50%;
}


/* =========================================================
   LEFT
========================================================= */

.left {
  position: relative;
  z-index: 2;

  min-width: 0;
  flex: 1;

  display: flex;
  align-items: center;

  gap: 10px;
}


/* =========================================================
   AI ICON
========================================================= */

.logo-shell {
  width: 34px;
  height: 34px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  background:
    linear-gradient(
      145deg,
      rgba(83, 105, 238, 0.08),
      rgba(80, 124, 240, 0.03)
    );

  border:
    1px solid
    rgba(73, 112, 226, 0.08);

  border-radius: 11px;
}


.logo {
  width: 23px;
  height: 23px;

  display: block;

  object-fit: contain;
}


/* =========================================================
   TEXT
========================================================= */

.text-block {
  min-width: 0;
  flex: 1;

  display: flex;
  flex-direction: column;
  justify-content: center;
}


.title-row {
  min-width: 0;

  display: flex;
  align-items: center;

  gap: 6px;
}


/* =========================================================
   TITLE
========================================================= */

.title {
  margin: 0;

  min-width: 0;

  color: #252930;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 12px;

  line-height: 1.2;

  letter-spacing:
    -0.015em;

  white-space: nowrap;
}


/* =========================================================
   NEW BADGE
========================================================= */

.badge {
  flex-shrink: 0;

  padding:
    3px
    6px;

  color: #5868df;

  background:
    rgba(84, 101, 230, 0.09);

  border:
    1px solid
    rgba(84, 101, 230, 0.10);

  border-radius: 7px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 7px;

  line-height: 1;

  letter-spacing:
    0.055em;
}


/* =========================================================
   SUBTITLE
========================================================= */

.subtitle {
  width: 174px;

  margin:
    6px
    0
    0;

  color:
    #777d87;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;

  line-height: 1.32;

  letter-spacing:
    -0.005em;

  display:
    -webkit-box;

  -webkit-line-clamp:
    3;

  -webkit-box-orient:
    vertical;

  overflow:
    hidden;
}


/* =========================================================
   LOADING DOTS
========================================================= */

.dots {
  height: 6px;

  display: flex;
  align-items: center;

  gap: 3px;

  margin-top: 6px;
}


.dots span {
  width: 4px;
  height: 4px;

  background:
    #5976e9;

  border-radius:
    50%;

  animation:
    blink
    1.25s
    infinite
    both;
}


.dots span:nth-child(1) {
  animation-delay:
    0s;
}


.dots span:nth-child(2) {
  animation-delay:
    0.16s;
}


.dots span:nth-child(3) {
  animation-delay:
    0.32s;
}


/* =========================================================
   ROBOT
========================================================= */

.robot {
  position: relative;
  z-index: 2;

  width: 72px;
  height: 100px;

  flex-shrink: 0;

  align-self:
    flex-end;

  object-fit:
    contain;

  margin-right:
    -20px;

  margin-bottom:
    -20px;

  transform:
    translateY(8px);

  transition:
    transform 0.25s ease;
}


.want-card:hover .robot {
  transform:
    translateY(6px)
    rotate(-1deg);
}


/* =========================================================
   DARK
========================================================= */

.want-card.theme-dark {
  background:
    linear-gradient(
      145deg,
      #22242a 0%,
      #1d1f24 100%
    );

  border-color:
    rgba(255, 255, 255, 0.065);

  box-shadow:
    0 12px 30px
    rgba(0, 0, 0, 0.15);
}


.want-card.theme-dark:hover {
  border-color:
    rgba(112, 157, 226, 0.13);

  box-shadow:
    0 16px 35px
    rgba(0, 0, 0, 0.22);
}


.want-card.theme-dark .card-glow {
  background:
    radial-gradient(
      circle,
      rgba(78, 130, 220, 0.14) 0%,
      rgba(78, 130, 220, 0.045) 45%,
      transparent 72%
    );
}


.want-card.theme-dark .logo-shell {
  background:
    rgba(92, 139, 216, 0.08);

  border-color:
    rgba(111, 158, 231, 0.08);
}


.want-card.theme-dark .title {
  color:
    #f0f1f3;
}


.want-card.theme-dark .subtitle {
  color:
    #858b95;
}


.want-card.theme-dark .badge {
  color:
    #9db9f0;

  background:
    rgba(95, 137, 220, 0.12);

  border-color:
    rgba(111, 158, 231, 0.10);
}


.want-card.theme-dark .dots span {
  background:
    #91b6ee;
}


.want-card.theme-dark .logo {
  filter:
    brightness(0)
    saturate(100%)
    invert(72%)
    sepia(18%)
    saturate(1250%)
    hue-rotate(178deg)
    brightness(103%);
}


.want-card.theme-dark .robot {
  filter:
    brightness(0.92)
    saturate(0.95);
}


/* =========================================================
   ANIMATION
========================================================= */

@keyframes blink {

  0% {
    opacity: 0.22;

    transform:
      translateY(0);
  }

  25% {
    opacity: 1;

    transform:
      translateY(-2px);
  }

  50% {
    opacity: 0.22;

    transform:
      translateY(0);
  }

}


/* =========================================================
   MOBILE
========================================================= */

@media (max-width: 430px) {

  .want-card {
    width:
      calc(100% - 20px);

    min-height:
      112px;

    margin-left:
      10px;
  }


  .subtitle {
    width:
      auto;

    max-width:
      190px;
  }


  .robot {
    width:
      76px;

    height:
      90px;
  }

}

</style>